<script setup lang="ts">
interface FilterOption {
  key: string
  label: string
  urlPath?: string
  toBeSelected?: boolean
  selected?: boolean
  count?: number
  swatch?: string
  [key: string]: unknown
}

interface Filter {
  key: string
  hasUrl?: boolean
  type?: string
  [key: string]: unknown
}

export type Props = {
  option: FilterOption
  filter: Filter
}

const props = defineProps<Props>()
defineEmits(['option-toggle'])

const label = computed(() => {
  return props.option.label
})

const filterUrlMap = useState<Record<string, string>>('filterUrlMap', () => ({}))

const filterUrl = computed(() =>
  filterUrlMap.value[`${props.filter.key}::${props.option.key}`] || null,
)

const { public: config } = useRuntimeConfig()

function swatchBg(option: FilterOption) {
  if (option.swatch?.startsWith('#')) {
    return {
      backgroundColor: option.swatch,
    }
  }

  if (option.swatch) {
    const swatchPath = config.baseDomain + '/media/attribute/swatch'
    return {
      backgroundImage: `url('${swatchPath + option.swatch}')`,
    }
  }
}
</script>

<template>
  <li class="filter-option">
    <a
      v-if="filterUrl || (filter.hasUrl && option.urlPath)"
      :href="filterUrl ? '/' + filterUrl + '/' : option.urlPath + '/'"
      class="filter-option__link"
      :class="{ 'filter-option__link--active': option.toBeSelected }"
      @click.prevent="$emit('option-toggle', option.key)"
    >
      <span
        class="filter-option__input checkbox"
        :class="{ 'checkbox--checked': option.selected }"
      />

      <span class="filter-option__label">
        <span
          v-if="filter.type === 'visual'"
          class="filter-option__swatch"
          :style="swatchBg(option)"
        />
        <span class="filter-option__name">{{ label }}</span>
        <span class="filter-option__count">{{ option.count }}</span>
      </span>
    </a>
    <template v-else>
      <input
        :id="`filter-${filter.key}-${option.key}`"
        type="checkbox"
        class="filter-option__input"
        :checked="option.selected"
        @input="$emit('option-toggle', option.key)"
      >
      <label
        class="filter-option__label"
        :for="`filter-${filter.key}-${option.key}`"
      >
        <span
          v-if="filter.type === 'visual'"
          class="filter-option__swatch"
          :style="swatchBg(option)"
        />
        <span class="filter-option__name">{{ label }}</span>
        <span class="filter-option__count">{{ option.count }}</span>
      </label>
    </template>
  </li>
</template>

<style>
@import url("~layers/app/components/Filter/Option.css");
</style>
